/*
 * SPIKE — cart mutual-exclusion guard
 * ---------------------------------------------------------------------------
 * Keeps configured products from being purchased in the same order.
 * Currently: MFM-0.1 and MB-0.2 cannot be in the cart at the same time.
 *
 * Soft, theme-level enforcement in two layers:
 *   1. Block-on-add — add-to-cart paths ask this guard before adding. If the
 *      "other" member of a group is already in the cart, the add is refused
 *      and an explanation is shown (block & explain). On this theme that means
 *      both the Dawn <product-form> (product-form.js) and the custom presale
 *      hero add-to-cart (sections/presale-hero.liquid).
 *   2. Checkout backstop — if both members ever co-exist anyway (a cart saved
 *      before this shipped, a /cart permalink, etc.), the checkout buttons are
 *      disabled and the same message is shown until one is removed.
 *
 * Config (product IDs, variant IDs, message) is rendered from Liquid into
 * window.SpikeCartPairGuardConfig in layout/theme.liquid, so the variant IDs
 * stay in sync with the store automatically. This file holds only logic.
 *
 * NOTE: this is storefront JS — it covers normal shopping but can't stop a
 * determined user hitting /cart/add directly. For a hard guarantee, a Cart &
 * Checkout Validation Function (Shopify Function) would be required.
 */
(function () {
  var config = window.SpikeCartPairGuardConfig;
  if (!config || !Array.isArray(config.groups) || config.groups.length === 0) return;

  var ENFORCE_AT_CHECKOUT = config.enforceAtCheckout !== false; // backstop on by default
  var MESSAGE = config.message || 'These products can’t be purchased in the same order.';

  // --- lookups --------------------------------------------------------------
  // Each group is a list of members: { productId, variantIds: [...] }.
  var variantToGroup = {}; // variantId  -> group index
  var variantToProduct = {}; // variantId  -> productId
  var productToGroup = {}; // productId  -> group index

  config.groups.forEach(function (members, gi) {
    members.forEach(function (member) {
      if (member.productId != null) productToGroup[String(member.productId)] = gi;
      (member.variantIds || []).forEach(function (vid) {
        variantToGroup[String(vid)] = gi;
        variantToProduct[String(vid)] = String(member.productId);
      });
    });
  });

  function isGuardedVariant(variantId) {
    return Object.prototype.hasOwnProperty.call(variantToGroup, String(variantId));
  }

  function getCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // True if the cart already holds two different members of the same group.
  function cartHasConflict(cart) {
    var seenByGroup = {};
    return (cart.items || []).some(function (item) {
      var pid = String(item.product_id);
      if (!Object.prototype.hasOwnProperty.call(productToGroup, pid)) return false;
      var gi = productToGroup[pid];
      var seen = (seenByGroup[gi] = seenByGroup[gi] || {});
      seen[pid] = true;
      return Object.keys(seen).length > 1;
    });
  }

  // --- layer 1: block-on-add ------------------------------------------------
  // Resolves to a conflict message if adding `variantId` would put two members
  // of the same group in the cart, otherwise null. Fails open on network error
  // (the checkout backstop still applies).
  function conflictMessageForVariant(variantId) {
    if (!isGuardedVariant(variantId)) return Promise.resolve(null);
    var gi = variantToGroup[String(variantId)];
    var ownProductId = variantToProduct[String(variantId)];
    return getCart()
      .then(function (cart) {
        var conflict = (cart.items || []).some(function (item) {
          var pid = String(item.product_id);
          return productToGroup[pid] === gi && pid !== ownProductId;
        });
        return conflict ? MESSAGE : null;
      })
      .catch(function () {
        return null;
      });
  }

  // --- layer 2: checkout backstop ------------------------------------------
  var CHECKOUT_SELECTOR = '#checkout, [name="checkout"]';
  var DYNAMIC_SELECTOR = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons';
  var NOTICE_CLASS = 'spike-pair-guard-notice';

  function setCheckoutBlocked(blocked) {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      if (blocked) {
        // remember the button's own disabled state (e.g. empty cart) once
        if (!btn.hasAttribute('data-spike-was-disabled')) {
          btn.setAttribute('data-spike-was-disabled', btn.disabled ? '1' : '0');
        }
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      } else if (btn.hasAttribute('data-spike-was-disabled')) {
        // only restore buttons WE disabled, back to their prior state
        btn.disabled = btn.getAttribute('data-spike-was-disabled') === '1';
        btn.setAttribute('aria-disabled', btn.disabled ? 'true' : 'false');
        btn.removeAttribute('data-spike-was-disabled');
      }
    });

    document.querySelectorAll(DYNAMIC_SELECTOR).forEach(function (box) {
      box.style.opacity = blocked ? '0.4' : '';
      box.style.pointerEvents = blocked ? 'none' : '';
    });

    renderNotices(blocked);
  }

  function renderNotices(show) {
    document.querySelectorAll('.' + NOTICE_CLASS).forEach(function (n) {
      n.remove();
    });
    if (!show) return;

    var anchors = [];
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      var anchor = btn.closest('.cart__ctas') || btn.closest('form') || btn;
      if (anchors.indexOf(anchor) === -1) anchors.push(anchor);
    });

    anchors.forEach(function (anchor) {
      var notice = document.createElement('div');
      notice.className = NOTICE_CLASS;
      notice.setAttribute('role', 'alert');
      notice.textContent = MESSAGE;
      notice.style.cssText =
        'margin:0 0 1rem;padding:1rem 1.2rem;border:1px solid currentColor;' +
        'border-radius:.6rem;font-size:1.3rem;line-height:1.4;';
      if (anchor.parentNode) anchor.parentNode.insertBefore(notice, anchor);
    });
  }

  function enforceCheckout() {
    if (!ENFORCE_AT_CHECKOUT) return;
    getCart()
      .then(function (cart) {
        var conflict = cartHasConflict(cart);
        setCheckoutBlocked(conflict);
        // re-apply after any section re-render that may have swapped the buttons
        requestAnimationFrame(function () {
          setCheckoutBlocked(conflict);
        });
      })
      .catch(function () {});
  }

  // --- public API + wiring --------------------------------------------------
  window.SpikeCartPairGuard = {
    isGuarded: isGuardedVariant,
    conflictMessageForVariant: conflictMessageForVariant,
    refresh: enforceCheckout,
  };

  function init() {
    enforceCheckout();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, enforceCheckout);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
